import { useEffect, useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias } from "resources/account"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { MODULE_TYPES, type DashboardModule } from "../modules/types"
import { useDeletePinnedWorkflow } from "connected-components/ai-teammate/hooks/use-delete-pinned-workflow"
import { useModules } from "../context/modules"
import { log } from "helpers/logger"
import { warn } from "helpers/dev-logger"

/**
 * Hook that listens for module removal events and handles cleanup for AI artifacts
 */
export function useRemoveArtifactHandler() {
  const { mutateAsync: deletePinnedWorkflow } = useDeletePinnedWorkflow()
  const accountAlias = useAccountAlias()
  const queryClient = useQueryClient()
  const { modules } = useModules()

  const handleModuleRemoved = useCallback(async (eventModule: DashboardModule) => {
    if (eventModule.type !== MODULE_TYPES.AI_ARTIFACT) {
      return
    }

    const currentModule = modules.find((m) => m.id === eventModule.id)
    const module = currentModule || eventModule
    const pinnedId = module.config?.pinnedId
    const isPinning = module.config?.isPinning

    if (pinnedId) {
      queryClient.removeQueries({ queryKey: ["ai-artifact", pinnedId] })

      try {
        await deletePinnedWorkflow({
          accountAlias,
          pinnedId,
        })
      } catch (error) {
        log(`Failed to delete pinned workflow: ${error}`)
      }
    }

    if (isPinning) {
      warn("AI artifact module removed while pin was in progress")
    }
  }, [deletePinnedWorkflow, accountAlias, queryClient, modules])

  useEffect(() => {
    const cleanupFn = dashboardEvents.on<DashboardModule>(
      DASHBOARD_EVENTS.MODULE_REMOVED,
      handleModuleRemoved,
    )

    return cleanupFn
  }, [handleModuleRemoved])
}
